import { useCallback } from 'react';
import { formatVersionTime } from './utils';
import { VersionCardProps } from './types';
import Avatar from '@/components/atoms/Avatar/Avatar';

export default function VersionCard({
  version,
  isCurrentVersion,
  isViewingVersion,
  onView,
  onRestore
}: VersionCardProps) {
  const authorName = `${version.created_by.first_name} ${version.created_by.last_name}`.trim();
  const isOriginal = version.version_index === 0;
  const versionLabel = isOriginal ? 'Original' : `Version ${version.version_index}`;

  const handleView = useCallback(() => {
    onView(version);
  }, [onView, version]);

  const handleRestore = useCallback(() => {
    onRestore(version);
  }, [onRestore, version]);

  return (
    <div
      className={`group flex cursor-pointer items-start gap-2 rounded-lg px-3 py-2 ${isViewingVersion ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
    >
      {/* Avatar — 32px to match design */}
      <div className="shrink-0">
        <Avatar
          name={authorName || version.created_by.email}
          imageUrl={version.created_by.avatar_url}
          size="md"
          imageClassName="rounded-full"
        />
      </div>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {/* Title row */}
        <div className="flex items-center justify-between gap-2">
          {/* Version name + badges */}
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="whitespace-nowrap text-sm font-semibold tracking-[-0.14px] text-black">
              {versionLabel}
            </span>

            {isCurrentVersion && !isViewingVersion && (
              <span className="whitespace-nowrap rounded-full bg-green-100 px-1.5 py-px text-[10px] font-medium leading-[13px] text-green-700">
                Current
              </span>
            )}

            {isOriginal && (
              <span className="whitespace-nowrap rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-medium leading-[13px] text-slate-600">
                Original
              </span>
            )}

            {isViewingVersion && (
              <span className="whitespace-nowrap rounded-full bg-yellow-100 px-1.5 py-px text-[10px] font-medium leading-[13px] text-yellow-700">
                Viewing
              </span>
            )}
          </div>

          {/* Action button */}
          <div className="shrink-0">
            {isViewingVersion ? (
              // Restore — always visible while in viewing state
              <button
                onClick={handleRestore}
                className="flex h-6 min-w-[36px] items-center justify-center rounded-md border border-slate-200 bg-white px-1 text-xs font-medium text-black"
                title={`Restore to Version ${version.version_index}`}
              >
                Restore
              </button>
            ) : (
              // View — appears on card hover, hidden for the current version
              !isCurrentVersion && (
                <button
                  onClick={handleView}
                  className="flex h-6 min-w-[36px] items-center justify-center rounded-md border border-slate-200 bg-white px-1 text-xs font-medium text-black opacity-0 transition-opacity group-hover:opacity-100"
                  title={`Preview Version ${version.version_index}`}
                >
                  View
                </button>
              )
            )}
          </div>
        </div>

        {/* Description */}
        <p className="text-sm leading-5 text-gray-500">
          {typeof version.is_restored_from === 'number'
            ? `This is a restore of Version ${version.is_restored_from}`
            : version.description}
        </p>

        {/* Timestamp */}
        <span className="text-[10px] font-medium leading-[13px] text-[#ff8032]">
          {formatVersionTime(version.created_at)}
        </span>
      </div>
    </div>
  );
}
